/**
 * Host-drawn widget settings dialog.
 *
 * Every widget gets this for free: the host reads `manifest.widget.settings`
 * and renders the controls itself, so a widget declaring a schema never writes
 * dialog chrome. Reuses the `.os-modal-scrim` / `.os-modal` pattern (and the
 * Esc + scrim-click close behaviour) from `openEditorPrefsDialog` in ui-host.ts.
 *
 * Changes apply LIVE — there is no OK/Cancel. Each control's `onChange` writes
 * straight through `osStore.setWidgetSetting`, which persists and emits, so the
 * widget behind the dialog updates as you drag.
 */
import type { ModuleContext, ModuleManifest, SettingSpec } from "@arclog/module-sdk";
import { osStore } from "./store";
import { uiApi } from "./api";
import { resolveSettings, visibleSpecs } from "./settings.mjs";
import { buildSettingRow, wrapSettingRow, type RowHandle } from "./settings-form";

export type WidgetSettingsDialogOptions = {
  /** Placement id ("it_<n>") — settings are keyed per placement, not per module. */
  placementId: string;
  manifest: ModuleManifest;
  surface: "desktop" | "panel";
  /**
   * The module's optional escape hatch, plus the ctx to hand it. Passed by
   * WidgetHost from the build actually running, since a manifest alone can't
   * carry a function.
   */
  renderSettings?: (el: HTMLElement, ctx: ModuleContext) => void | Promise<void>;
  ctx?: ModuleContext;
};

type AnyHandle = RowHandle;

/** Offered sizes for type:"size", before manifest min* / grid-width filtering. */
const SIZE_PRESETS = [
  { cols: 2, rows: 2 },
  { cols: 4, rows: 2 },
  { cols: 2, rows: 4 },
  { cols: 4, rows: 4 },
];

export function openWidgetSettingsDialog(opts: WidgetSettingsDialogOptions): void {
  const { placementId, manifest, surface } = opts;
  const specs: SettingSpec[] = manifest.widget?.settings || [];

  const scrim = document.createElement("div");
  scrim.className = "os-modal-scrim";
  const modal = document.createElement("div");
  modal.className = "os-modal os-wsd";
  scrim.appendChild(modal);

  /** Live control handles for the current row set; destroyed on every re-render. */
  const handles: AnyHandle[] = [];
  const destroyHandles = () => {
    for (const h of handles.splice(0)) {
      try {
        h.destroy();
      } catch {
        /* a control that fails to tear down must not strand the rest */
      }
    }
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  function close() {
    document.removeEventListener("keydown", onKey);
    destroyHandles();
    scrim.remove();
  }
  scrim.addEventListener("mousedown", (e) => {
    if (e.target === scrim) close();
  });
  document.addEventListener("keydown", onKey);

  const head = document.createElement("div");
  head.className = "os-modal-h";
  head.textContent = manifest.widget?.title || manifest.name;

  const body = document.createElement("div");
  body.className = "os-modal-b os-wsd-b";

  const foot = document.createElement("div");
  foot.className = "os-modal-f os-wsd-f";

  /* The module's escape hatch. Built ONCE and re-appended after each re-render:
     rebuilding it whenever a showWhen row toggles would wipe whatever state the
     module put in there. */
  const customHost = document.createElement("div");
  customHost.className = "os-wsd-custom";

  /* Keys that gate another row's visibility. ONLY these force a re-render —
     re-rendering on every write would rip a slider out from under the pointer
     mid-drag. */
  const controllers = new Set<string>();
  for (const s of specs) if (s.showWhen) controllers.add(s.showWhen);

  const write = (key: string, value: unknown) => {
    osStore.setWidgetSetting(placementId, key, value);
    if (controllers.has(key)) renderRows();
  };

  function renderRows() {
    destroyHandles();
    body.innerHTML = "";
    const values = resolveSettings(specs, osStore.getWidgetSettings(placementId));
    let rendered = 0;
    for (const s of visibleSpecs(specs, values)) {
      const row = buildRow(s, values[s.key]);
      if (!row) continue;
      body.appendChild(row);
      rendered += 1;
    }
    if (!rendered && !customHost.childNodes.length) {
      const empty = document.createElement("div");
      empty.className = "os-wsd-empty";
      empty.textContent = "This widget has no settings.";
      body.appendChild(empty);
    }
    body.appendChild(customHost);
  }

  function buildRow(s: SettingSpec, v: unknown): HTMLElement | null {
    // Size is host-owned and needs the placement + grid, so it stays here;
    // everything else is the shared renderer.
    if (s.type === "size") return buildSizeRow(s);
    const built = buildSettingRow({
      spec: s,
      value: v,
      prefix: "os-wsd",
      onChange: (val) => write(s.key, val),
    });
    if (!built) return null;
    handles.push(built.handle);
    return built.row;
  }

  /**
   * Size is host-owned: it writes cols/rows onto the placement, never into the
   * settings bag (which is why resolveSettings skips type:"size" entirely).
   */
  function buildSizeRow(s: SettingSpec): HTMLElement | null {
    // The panel stacks a fixed width, so a size control there would be a lie.
    if (surface === "panel") return null;
    const minCols = manifest.widget?.minCols ?? 1;
    const minRows = manifest.widget?.minRows ?? 1;
    // Pre-filtered to the live grid width so the presets on offer are ones the
    // placement policy can actually honour — the store still refuses a preset
    // that no longer fits on a crowded desktop.
    const gridCols = osStore.gridCols();
    const options = SIZE_PRESETS.filter(
      (p) => p.cols >= minCols && p.rows >= minRows && p.cols <= gridCols
    ).map((p) => ({ value: `${p.cols}x${p.rows}`, label: `${p.cols} × ${p.rows}` }));
    if (!options.length) return null;

    const current = () => {
      const it = osStore.desktopItems.find((d) => d.id === placementId);
      return it ? `${it.cols}x${it.rows}` : "";
    };
    // The tile's current size may not be one of the presets (e.g. a 1×1
    // default), so offer it rather than silently showing someone else's value.
    const cur = current();
    if (cur && !options.some((o) => o.value === cur))
      options.unshift({ value: cur, label: cur.replace("x", " × ") + " (current)" });

    const handle = uiApi.select({
      options,
      label: s.label,
      value: cur || options[0].value,
      onChange: (v: string) => {
        const [c, r] = v.split("x").map(Number);
        if (!Number.isFinite(c) || !Number.isFinite(r)) return;
        osStore.resizeDesktopItem(placementId, c, r);
        // resizeDesktopItem can REFUSE (no room). Snap the select back to what
        // the placement actually is, so the control never claims a size the
        // desktop rejected.
        handle.setValue(current());
      },
    });
    handles.push(handle);
    return wrapRow(handle.el, s.help);
  }

  // Declaration, not a const: buildSizeRow above calls this, and a const would
  // sit in the temporal dead zone until this line ran.
  function wrapRow(control: HTMLElement, help?: string): HTMLElement {
    return wrapSettingRow(control, "os-wsd", help);
  }

  function customError(err: unknown) {
    const e = document.createElement("div");
    e.className = "os-wsd-error";
    e.textContent = "⚠ settings panel failed: " + String((err as Error)?.message || err);
    customHost.appendChild(e);
    // eslint-disable-next-line no-console
    console.warn("[widget-settings]", manifest.id, err);
  }

  // Escape hatch, beneath the schema rows. A throwing renderSettings shows an
  // inline error and leaves every schema control working.
  if (typeof opts.renderSettings === "function" && opts.ctx) {
    try {
      const r = opts.renderSettings(customHost, opts.ctx);
      if (r && typeof (r as Promise<void>).then === "function")
        void (r as Promise<void>).catch(customError);
    } catch (err) {
      customError(err);
    }
  }

  renderRows();

  foot.appendChild(
    uiApi.button({
      label: "Reset to defaults",
      variant: "ghost",
      onClick: () => {
        osStore.resetWidgetSettings(placementId);
        renderRows();
      },
    })
  );
  foot.appendChild(uiApi.button({ label: "Done", variant: "primary", onClick: close }));

  modal.append(head, body, foot);
  document.body.appendChild(scrim);
}
